import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * <inside-free-gift>
 *
 * Ajoute et retire automatiquement la cuillere offerte selon la presence de la
 * variante declenchante (cure 3 mois) dans le panier.
 *
 * L'ajout est reserve aux visites venues du bouton de l'e-mail, reconnues de
 * deux facons :
 *
 * 1. Le code de reduction de la campagne est au panier. Le bouton de l'e-mail
 *    pointe sur /discount/CUILLERE-INSIDE?redirect=..., qui pose le code cote
 *    Shopify des le clic ; /cart.js le rend alors lisible, panier vide compris.
 *    C'est la voie principale : elle ne depend d'aucun stockage navigateur, elle
 *    survit aux changements de page, et elle honore les e-mails deja envoyes.
 * 2. A defaut, un marqueur dans l'URL (`?inside_gift=1` par defaut), memorise
 *    puis efface de la barre d'adresse. Utile pour une campagne sans code.
 *
 * Sans l'un ou l'autre, une cure 3 mois ajoutee a la main ne declenche rien.
 * Ce filtre est ergonomique, pas etanche, et n'a pas besoin de l'etre : c'est la
 * reduction Shopify qui decide de la gratuite, revalidee a la commande.
 *
 * CE MODULE N'EST PAS UN DISPOSITIF DE SECURITE.
 *
 * La cuillere est ajoutee a son prix reel. Sa gratuite est decidee, et
 * revalidee a la creation de la commande, par la reduction Shopify
 * « Achetez X, obtenez Y ». Si ce script est desactive, bloque ou manipule, le
 * pire resultat possible est un panier mal garni : une cuillere ajoutee a la
 * main sans la cure 3 mois reste facturee a son prix normal, et une seconde
 * cuillere n'est jamais offerte. Le theme ne touche jamais a un prix.
 *
 * Le marquage de ligne `_inside_gift` sert uniquement a distinguer la ligne que
 * ce module a ajoutee de celle que le client aurait ajoutee lui-meme : on ne
 * retire jamais d'office une ligne dont on n'est pas l'auteur. Ce n'est pas une
 * preuve d'eligibilite, et Shopify ne s'en sert pas.
 */

const SOURCE = 'inside-free-gift';
const GIFT_PROPERTY = '_inside_gift';

/** Memoire de session : purement ergonomique, jamais une regle metier. */
const STORAGE_ADDED = 'inside:gift-added';
const STORAGE_OPT_OUT = 'inside:gift-opt-out';

/**
 * Deverrouillage par le lien de l'e-mail. localStorage et non sessionStorage :
 * un client qui clique le lien, ferme l'onglet, puis revient finir sa commande
 * garde son droit a la cuillere. La date d'expiration est stockee telle quelle,
 * pour qu'une campagne ne deverrouille pas le navigateur a vie.
 */
const STORAGE_UNLOCKED = 'inside:gift-unlocked';
const UNLOCK_TTL_MS = 30 * 24 * 60 * 60 * 1000;

/** Nom de parametre retenu quand le reglage de theme est vide. */
const DEFAULT_UNLOCK_PARAM = 'inside_gift';

/**
 * sessionStorage jette en navigation privee sur certains navigateurs, et le
 * module doit continuer a fonctionner sans lui.
 *
 * @param {string} key
 * @returns {boolean}
 */
function flagRead(key) {
  try {
    return sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

/**
 * @param {string} key
 * @param {boolean} value
 */
function flagWrite(key, value) {
  try {
    if (value) sessionStorage.setItem(key, '1');
    else sessionStorage.removeItem(key);
  } catch {
    // Sans stockage, on retombe sur le comportement par defaut : reajout.
  }
}

/**
 * @returns {boolean} Le lien de l'e-mail a ete suivi, et le deverrouillage n'a
 *   pas expire.
 */
function unlockRead() {
  try {
    const expiresAt = Number.parseInt(localStorage.getItem(STORAGE_UNLOCKED) ?? '', 10);
    if (!Number.isFinite(expiresAt)) return false;

    if (Date.now() > expiresAt) {
      localStorage.removeItem(STORAGE_UNLOCKED);
      return false;
    }

    return true;
  } catch {
    // Stockage indisponible : on retombe du cote restrictif, pas d'ajout.
    return false;
  }
}

function unlockWrite() {
  try {
    localStorage.setItem(STORAGE_UNLOCKED, String(Date.now() + UNLOCK_TTL_MS));
  } catch {
    // Sans stockage, le deverrouillage ne vaut que pour le chargement en cours.
  }
}

/**
 * Journal de diagnostic, actif seulement sur demande : ajouter `?gift_debug=1`
 * a l'URL, ou poser `window.INSIDE_GIFT_DEBUG = true` avant le chargement.
 * Un panier silencieux est indiscernable d'un module absent sans ca.
 */
const DEBUG = (() => {
  try {
    if (window.INSIDE_GIFT_DEBUG) return true;
    return new URLSearchParams(window.location.search).get('gift_debug') === '1';
  } catch {
    return false;
  }
})();

/**
 * @param {string} message
 * @param {...unknown} details
 */
function log(message, ...details) {
  if (DEBUG) console.log(`[${SOURCE}] ${message}`, ...details);
}

class InsideFreeGift extends HTMLElement {
  /** @type {boolean} Un cycle de reconciliation est en cours. */
  #busy = false;

  /** @type {boolean} Un evenement est arrive pendant le cycle en cours. */
  #pending = false;

  /** @type {number} */
  #giftVariantId = 0;

  /** @type {Set<number>} */
  #triggerIds = new Set();

  /** @type {{ add: string, change: string, cart: string }} */
  #routes = { add: '/cart/add', change: '/cart/change', cart: '/cart' };

  /** @type {boolean} L'ajout est reserve aux visites venues du lien e-mail. */
  #emailOnly = true;

  /** @type {string} */
  #unlockParam = DEFAULT_UNLOCK_PARAM;

  /** @type {string} Valeur exigee dans l'URL, vide = n'importe laquelle. */
  #unlockValue = '';

  /** @type {Set<string>} Codes de reduction valant preuve de provenance. */
  #unlockCodes = new Set();

  /**
   * @type {boolean} Deverrouillage constate pendant ce chargement de page. Sert
   * de secours quand localStorage jette (navigation privee) : la visite venue du
   * lien reste servie, elle ne survit simplement pas au rechargement.
   */
  #unlockedNow = false;

  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';

    this.#giftVariantId = Number.parseInt(this.dataset.giftVariantId ?? '', 10) || 0;
    this.#triggerIds = new Set(
      (this.dataset.triggerVariantIds ?? '')
        .split(',')
        .map((value) => Number.parseInt(value.trim(), 10))
        .filter((value) => Number.isInteger(value) && value > 0)
    );

    log('initialisation', {
      variante_cadeau: this.#giftVariantId,
      variantes_declencheuses: [...this.#triggerIds],
    });

    // Sans identifiants exploitables, le module reste inerte plutot que de
    // deviner un produit par son titre.
    if (!this.#giftVariantId || this.#triggerIds.size === 0) {
      console.warn(`[${SOURCE}] Identifiants de variante inexploitables, module inerte.`);
      return;
    }

    if (this.#triggerIds.has(this.#giftVariantId)) {
      console.warn(`[${SOURCE}] La variante offerte ne peut pas etre sa propre declencheuse.`);
      return;
    }

    this.#routes = {
      add: this.dataset.cartAddUrl || '/cart/add',
      change: this.dataset.cartChangeUrl || '/cart/change',
      cart: this.dataset.cartUrl || '/cart',
    };

    // Seul un « false » explicite ouvre l'ajout a toutes les visites : un
    // attribut absent doit fermer, jamais ouvrir.
    this.#emailOnly = this.dataset.emailOnly !== 'false';
    this.#unlockParam = (this.dataset.unlockParam || '').trim() || DEFAULT_UNLOCK_PARAM;
    this.#unlockValue = (this.dataset.unlockValue || '').trim();
    this.#unlockCodes = new Set(
      (this.dataset.unlockDiscounts ?? '')
        .split(',')
        .map((code) => code.trim().toUpperCase())
        .filter(Boolean)
    );

    this.#captureUnlock();

    log('reservation au lien e-mail', {
      active: this.#emailOnly,
      parametre: this.#unlockParam,
      valeur_exigee: this.#unlockValue || '(indifferente)',
      codes_acceptes: [...this.#unlockCodes],
      deverrouille_par_l_url: this.#isUnlocked(),
    });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);

    // Rechargement de page avec la cure deja au panier : on rattrape l'etat.
    this.#sync();
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
  }

  /**
   * Lit le marqueur du lien e-mail dans l'URL, le memorise, puis l'efface de la
   * barre d'adresse : le client ne republie pas un lien de campagne sans le
   * savoir, et un rechargement apres expiration ne redeverrouille pas.
   */
  #captureUnlock() {
    if (!this.#emailOnly) return;

    let params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch {
      return;
    }

    const value = params.get(this.#unlockParam);
    if (value === null) return;

    if (this.#unlockValue && value !== this.#unlockValue) {
      log('marqueur present mais valeur inattendue, deverrouillage refuse', { recu: value });
      return;
    }

    this.#unlockedNow = true;
    unlockWrite();
    log('deverrouillage par le lien e-mail memorise');

    try {
      params.delete(this.#unlockParam);
      const query = params.toString();
      window.history.replaceState(
        window.history.state,
        '',
        `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`
      );
    } catch {
      // URL inchangee : sans consequence, le marqueur reste simplement visible.
    }
  }

  /** @returns {boolean} L'URL de la visite a deverrouille l'ajout. */
  #isUnlocked() {
    if (!this.#emailOnly) return true;
    return this.#unlockedNow || unlockRead();
  }

  /**
   * Le panier porte un des codes de la campagne. Shopify pose le code des le
   * clic sur /discount/..., donc il est deja lisible quand le client ajoute sa
   * cure, panier vide compris.
   *
   * On teste la presence du code, jamais son `applicable` : tant que la cuillere
   * n'est pas au panier, la reduction « Achetez X, obtenez Y » n'a rien a
   * reduire et Shopify renvoie `applicable: false`. S'y fier rendrait la
   * condition impossible a satisfaire — c'est precisement l'ajout qu'elle garde.
   *
   * @param {any} cart Reponse de /cart.js.
   * @returns {boolean}
   */
  #cartUnlocks(cart) {
    if (this.#unlockCodes.size === 0) return false;

    const codes = Array.isArray(cart?.discount_codes) ? cart.discount_codes : [];

    return codes.some((entry) => this.#unlockCodes.has(String(entry?.code ?? '').trim().toUpperCase()));
  }

  /**
   * Le theme diffuse l'evenement AVANT que la requete panier n'aboutisse (voir
   * assets/inside-cure-picker.js et assets/product-form.js), d'ou l'attente de
   * `event.promise` : sans elle on lirait le panier d'avant la mutation.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #onCartLinesUpdate = (event) => {
    // Nos propres mutations : ne pas se repondre a soi-meme.
    if (event.target === this) return;

    log('evenement panier recu', { action: event.action, emetteur: event.target?.tagName });

    if (this.#busy) {
      this.#pending = true;
      return;
    }

    Promise.resolve(event.promise)
      .then(() => this.#sync())
      .catch(() => {
        // Mutation en echec cote theme : le panier n'a pas bouge, rien a faire.
      });
  };

  /**
   * Un seul cycle a la fois. Les evenements recus pendant le cycle ne s'empilent
   * pas : ils rejouent une derniere passe a la fin. C'est ce qui empeche la
   * boucle entre notre propre ajout et l'evenement qu'il declenche.
   */
  async #sync() {
    if (this.#busy) {
      this.#pending = true;
      return;
    }

    this.#busy = true;
    try {
      do {
        this.#pending = false;
        await this.#reconcile();
      } while (this.#pending);
    } catch (error) {
      console.warn(`[${SOURCE}]`, error);
    } finally {
      this.#busy = false;
    }
  }

  /**
   * Compare l'etat du panier a l'etat attendu et n'emet au plus qu'une mutation.
   * Toute divergence restante sera traitee au tour suivant de la boucle.
   */
  async #reconcile() {
    const cart = await this.#fetchCart();
    const items = Array.isArray(cart?.items) ? cart.items : [];

    const hasTrigger = items.some((item) => this.#triggerIds.has(item.variant_id ?? item.id));
    const unlockedByCart = this.#cartUnlocks(cart);
    const unlocked = this.#isUnlocked() || unlockedByCart;
    const giftLines = items.filter((item) => (item.variant_id ?? item.id) === this.#giftVariantId);
    const ownGiftLine = giftLines.find((item) => item.properties?.[GIFT_PROPERTY]);

    log('etat du panier', {
      lignes: items.map((item) => ({
        variante: item.variant_id ?? item.id,
        titre: item.title,
        qte: item.quantity,
      })),
      declencheur_present: hasTrigger,
      cuilleres_presentes: giftLines.length,
      cuillere_posee_par_nous: Boolean(ownGiftLine),
      deverrouille: unlocked,
      deverrouille_par_le_code: unlockedByCart,
      codes_au_panier: (cart?.discount_codes ?? []).map((entry) => entry?.code),
    });

    if (!hasTrigger) {
      // La cure 3 mois est partie (retrait, ou bascule vers la cure 1 mois) :
      // on ne retire que la ligne dont on est l'auteur.
      if (ownGiftLine) {
        await this.#mutateGift(0, () => this.#changeLine(ownGiftLine.key, 0));
      }

      // Le panier repart a zero cote cadeau : un nouvel ajout de cure doit
      // redonner la cuillere, meme si le client l'avait retiree auparavant.
      flagWrite(STORAGE_ADDED, false);
      flagWrite(STORAGE_OPT_OUT, false);
      return;
    }

    if (giftLines.length === 0) {
      // Visite ordinaire : la cure 3 mois ajoutee a la main ne donne rien. Le
      // retrait ci-dessus reste actif, lui, sinon une cuillere posee pendant une
      // visite deverrouillee resterait coincee au panier apres expiration.
      if (!unlocked) {
        log("ajout ignore : ni code de campagne au panier, ni marqueur d'URL");
        return;
      }

      // Cuillere absente alors que nous l'avions posee : c'est le client qui l'a
      // retiree. On respecte son geste pour le reste de la session plutot que de
      // la lui remettre de force a chaque mise a jour du panier.
      if (flagRead(STORAGE_ADDED)) {
        flagWrite(STORAGE_ADDED, false);
        flagWrite(STORAGE_OPT_OUT, true);
        return;
      }

      if (flagRead(STORAGE_OPT_OUT)) {
        log('retrait volontaire memorise pour cette session : pas de reajout');
        return;
      }

      log('ajout de la cuillere', { variante: this.#giftVariantId });
      await this.#mutateGift(1, () => this.#addLine());
      flagWrite(STORAGE_ADDED, true);
      log('cuillere ajoutee');
      return;
    }

    // Cuillere deja presente : jamais de seconde ligne, et jamais plus d'une
    // unite sur la notre, quel que soit le nombre de cures au panier.
    flagWrite(STORAGE_ADDED, true);

    if (ownGiftLine && ownGiftLine.quantity > 1) {
      await this.#mutateGift(1, () => this.#changeLine(ownGiftLine.key, 1));
    }
  }

  /**
   * Enveloppe une mutation dans la sequence d'evenements du theme, pour que le
   * tiroir et la page panier se rafraichissent comme pour n'importe quel autre
   * ajout. L'action reste `update` et jamais `add` : `add` ferait rouvrir le
   * tiroir par-dessus celui que l'ajout du client vient tout juste d'ouvrir.
   *
   * @param {number} quantity Quantite visee pour la ligne cadeau.
   * @param {() => Promise<Response>} run
   */
  async #mutateGift(quantity, run) {
    // La mutation aboutit avant toute diffusion : contrairement aux ajouts
    // declenches par un clic, il n'y a pas d'etat de chargement a afficher, et
    // un echec (cuillere en rupture passe les 100) ne doit pas laisser les
    // autres composants avec une promesse rejetee a signaler.
    const response = await run();
    const payload = await response.json();

    log('reponse Shopify', { http: response.status, corps: payload });

    // Les erreurs panier de Shopify arrivent en HTTP 4xx avec un champ `status`
    // dans le corps : `response.ok` seul les laisserait passer.
    if (payload?.status) {
      console.warn(`[${SOURCE}] Shopify a refuse la mutation :`, payload.description ?? payload.message, payload);
      throw payload;
    }

    const cart = await this.#fetchCart();

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        lines: [{ merchandiseId: String(this.#giftVariantId), quantity }],
        promise: deferred.promise,
      })
    );

    // Les abonnes ont attache leur `.then` pendant `dispatchEvent`.
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart?.items ?? [],
        source: SOURCE,
        sourceId: SOURCE,
        itemCount: 1,
      },
    });
  }

  /** @returns {Promise<Response>} */
  #addLine() {
    return fetch(`${this.#routes.add}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: [
          {
            id: this.#giftVariantId,
            quantity: 1,
            properties: { [GIFT_PROPERTY]: '1' },
          },
        ],
      }),
    });
  }

  /**
   * @param {string} key Cle de ligne renvoyee par /cart.js, et non son index,
   *   qui se decale des qu'une autre ligne du panier bouge.
   * @param {number} quantity
   * @returns {Promise<Response>}
   */
  #changeLine(key, quantity) {
    return fetch(`${this.#routes.change}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: key, quantity }),
    });
  }

  /** @returns {Promise<any>} */
  async #fetchCart() {
    const response = await fetch(`${this.#routes.cart}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });

    if (!response.ok) throw new Error(`Lecture du panier impossible : ${response.status}`);

    return response.json();
  }
}

if (!customElements.get('inside-free-gift')) {
  customElements.define('inside-free-gift', InsideFreeGift);
}
